import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, SelectPicker } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import ImageCell from '@/components/ImageCell';
import { useNavigate } from 'react-router-dom';
import PageContent from '@/components/PageContent';
import { BrandGear, BrandGearPaginationFilterRequest, GearTypeList } from '@/models/brand-gear.model';
import { getPagedBrandGears } from '@/services/brand-gear.service';
import FetchingSelect from '@/components/FetchingSelect';
import { getSportBrandsLookup } from '@/services/sport-brand.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface BrandGearListProps {
  sportBrandId?: number;
};

const BrandGearList : React.FC<BrandGearListProps> = ({sportBrandId}) => {
  const [filterReq, setFilterReq] = useState<BrandGearPaginationFilterRequest>(new BrandGearPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<BrandGear[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: BrandGearPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        sportBrandId: sportBrandId ?? req.sportBrandId,
      };        
      const result: PagedResponseModel<BrandGear> = await getPagedBrandGears(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching brand gears:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/brand-gears/new`)}>
          Add New Gear
        </Button>

        <Stack spacing={6}>
          {!sportBrandId && <FetchingSelect
            label="Sport Brand"
            fetchData={(text, page) => getSportBrandsLookup(text, page)}
            value={filterReq.sportBrandId}
            onChange={dataKey => setFilterReq(pre => ({...pre, sportBrandId: dataKey}))}
          />}
          <SelectPicker
            label="Type"
            data={GearTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre => ({...pre, type: dataKey ?? undefined}))}
          />                      
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Cover Image</HeaderCell>
          <ImageCell dataKey="coverImageUrl" />
        </Column>              
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey="type">
            {(rowData: BrandGear) => GearTypeList.findLast(i => i.value == rowData.type)?.label ?? '-'}
          </Cell>
        </Column>
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Sport Brand</HeaderCell>
          <Cell dataKey="sportBrand.name" />
        </Column>
               
        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData : BrandGear) => (              
              <IconButton onClick={() => nav(`/brand-gears/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default BrandGearList;
